import type { Order as GQLItem } from '../../../generated/internal.graphql';
import { take } from '../../../util';
import * as Account from '../Account/Account';
import * as Customer from '../Customer/Customer';
import * as Event from '../Event/Event';
import { toOrderState } from './OrderState';
import type { OrderState } from './OrderState';

/**
 * An order as the internal (VP staff) schema returns it. Structurally close to
 * `App/Order/Order` but not the same type, and deliberately so: the schemas are independent and the
 * internal `event` is far thinner. `OrderDetails` is a separate wire type, in `OrderDetail.ts`.
 *
 * Only the fields the staff order screens render are mapped. Line items, fees, totals, origin and
 * refund status are all available on the schema and all left out on purpose — an unmapped field
 * is one less thing to keep in step with a schema nobody outside VenuePilot can see.
 */
export type Order = {
  id: number;
  createdAt: string;
  updatedAt: string;
  state: OrderState;
  fingerprint: string;
  account: Account.Account;
  customer?: Customer.Customer;
  event?: Event.Event;
};

export const fromGql = (item: GQLItem): Order => ({
  ...take<Order>(item, 'id', 'createdAt', 'updatedAt', 'fingerprint'),
  state: toOrderState(item.state),
  account: Account.fromGql(item.account),
  customer: item.customer ? Customer.fromGql(item.customer) : undefined,
  event: item.event ? Event.fromGql(item.event) : undefined,
});
